import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Button, Modal, Select, TextInput } from "@opendate-app/setlist";

const NewCollectionModal = ({ open, onClose, createPath, teamId, venueOwnerships }) => {
  const [name, setName] = useState("");
  const [venueOwnershipId, setVenueOwnershipId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const venueOptions = [
    { value: "", label: "No venue" },
    ...venueOwnerships.map((venueOwnership) => ({ value: String(venueOwnership.id), label: venueOwnership.name })),
  ];

  const close = () => {
    if (submitting) return;
    setName("");
    setVenueOwnershipId("");
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (submitting || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(createPath, {
        event_series: { name: name.trim(), grouping: true, venue_ownership_id: venueOwnershipId || null },
      });
      window.location = `/teams/${teamId}/event_series/${data.id}`;
    } catch (e) {
      setError("Could not create the collection. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      icon={false}
      title="Name your collection"
      footer={(
        <>
          <Button onClick={close} disabled={submitting}>Cancel</Button>
          <Button variant="primary" data-testid="collection-submit" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? "Creating…" : "Create collection"}
          </Button>
        </>
      )}
    >
      <p className="collections-index-help">
        Group any confirmed events under one collection. You&rsquo;ll add shows next, and the collection&rsquo;s dates come from them.
      </p>
      {error && (
        <div className="mb-3">
          <Alert color="error" title={error} />
        </div>
      )}
      <div className="mb-3">
        <TextInput
          label="Name"
          id="collection-name"
          data-testid="collection-name"
          maxLength={255}
          placeholder="Festival name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
      </div>
      <Select
        id="collection-venue"
        label={<>Venue <span className="collections-index-muted">(optional)</span></>}
        options={venueOptions}
        value={venueOwnershipId}
        onChange={(value) => setVenueOwnershipId(value || "")}
        hint="Set a venue to sell passes on this collection — pass pricing, fees, and taxes are all derived from it."
      />
    </Modal>
  );
};

NewCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  createPath: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
};

export default NewCollectionModal;
